/**
 * SpiceKindMark: the marks that carry a SPICE session's kind in its
 * colour. SpiceKindDot is a small dot in the kind's fill, decoration only
 * (aria-hidden: the kind is named in words nearby). SpiceKindLegend is
 * the one line that says which colour is which: a dot and "SPICE", a dot
 * and "Friendly Visitor". Both read the owner's colours through
 * useSpiceKindColors (tenants.config.spice.kind_colors, with defaults),
 * so they must render inside the app's QueryClientProvider.
 */

import type { ReactNode } from 'react';
import { useSpiceKindColors } from '@/hooks/useSpiceKindColors';
import { SPICE_KIND_LABELS } from '@/lib/spiceScheduleCopy';
import type { SpiceSessionKind } from '@/lib/spiceVisitPlansApi';

interface SpiceKindMarkProps {
  kind: SpiceSessionKind;
  className?: string;
}

export function SpiceKindDot({ kind, className }: SpiceKindMarkProps): ReactNode {
  const colors = useSpiceKindColors();
  return (
    <span
      aria-hidden="true"
      data-testid="spice-kind-dot"
      data-kind={kind}
      className={`inline-block h-2 w-2 shrink-0 rounded-pill ${className ?? ''}`}
      style={{ backgroundColor: colors[kind].fill }}
    />
  );
}

/** The two kinds and their colours, in one quiet line. */
export function SpiceKindLegend({ testId }: { testId: string }): ReactNode {
  return (
    <p data-testid={testId} className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-ink-soft">
      <span data-testid={`${testId}-spice`} className="inline-flex items-center gap-1.5">
        <SpiceKindDot kind="spice" />
        {SPICE_KIND_LABELS.spice}
      </span>
      <span data-testid={`${testId}-fv`} className="inline-flex items-center gap-1.5">
        <SpiceKindDot kind="spice_friendly_visitor" />
        {SPICE_KIND_LABELS.spice_friendly_visitor}
      </span>
    </p>
  );
}
